import React, { MouseEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { subject } from '@casl/ability';
import {
  AtsSkeleton,
  Button,
  BUTTON_TYPES,
  Divider,
  notification,
  NOTIFICATION_TYPES,
  Status,
  Typography,
  TYPOGRAPHY_WEIGHT,
} from '_atoms';
import { Vector } from '_atoms/Icons';
import { ReactComponent as Accepted } from 'dist/icons/accepted.svg';
import { ReactComponent as Ai } from 'dist/icons/ai.svg';
import { ReactComponent as EmptyImg } from 'dist/emptyStates/stepsNoCandidates.svg';
import { ReactComponent as ErrorImg } from 'dist/icons/warning.svg';
import { ReactComponent as Info } from 'dist/icons/info.svg';
import { ReactComponent as Undo } from 'dist/icons/undo.svg';
import { ReactComponent as Edit } from 'dist/icons/edit.svg';
import './style.scss';
import { LoadingCard } from '_molecules/LoadingCard';
import { PopupRegenerateCandidateSummary } from '_molecules/PopupRegenerateCandidateSummary';
import { dateFormatter } from 'helpers/dateFormatter';
import { getCandidateSummary as loadCandidateSummary, generateCandidateSummary, updateCandidateSummary } from 'actions';
import { useAppDispatch, useAppSelector } from 'store';
import { getCandidateSummary } from 'store/selectors/jobSelectors';
import { clearCandidateSummary, type CandidateSummary } from 'store/reducers/jobReducer';
import { ACTIONS, Can, SUBJECTS, useAbility } from 'permission';
import {
  EMPTY_SUMMARY_VALUE,
  SUMMARY_ACCEPTED_ICON_VIEWBOX,
  SUMMARY_STATE_CONTENT,
  SUMMARY_STATUS_CONFIG,
  SUMMARY_STATUSES,
} from './constants';
import type {
  JobCandidateSummaryProps,
  SummaryEditValues,
  SummaryHeadingProps,
  SummaryIconListProps,
  SummaryState,
  SummaryStateProps,
} from './types';
import { formatSummarySources } from './utils';
import { SummaryEdit } from './SummaryEdit';

const SummaryHeading: React.FC<SummaryHeadingProps> = ({ status, generatedAt, showStatus = true }) => {
  const { t } = useTranslation();
  const statusConfig = SUMMARY_STATUS_CONFIG[status];
  const generatedAtDate = generatedAt ? dateFormatter(generatedAt, 'MMM DD, YYYY') : null;
  const label = [t(statusConfig.labelKey), generatedAtDate].filter(text => !!text).join(' ');

  return (
    <div className="job-candidate-summary__heading">
      <Ai width={24} height={24} />
      <Typography.Title2 weight={TYPOGRAPHY_WEIGHT.BOLD}>
        {t('translation:job_candidate_summary.title')}
      </Typography.Title2>
      {showStatus && <Status type={statusConfig.type}>{label}</Status>}
    </div>
  );
};

const SummaryState: React.FC<SummaryStateProps> = ({ state, canCreate, onGenerate }) => {
  const { t } = useTranslation();
  const { titleKey, descriptionKey } = SUMMARY_STATE_CONTENT[state];

  return (
    <div className="job-candidate-summary__state">
      {state === 'empty' ? (
        <EmptyImg className="empty-image" />
      ) : (
        <div className="error-image">
          <ErrorImg />
        </div>
      )}
      <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD} className="job-candidate-summary__state-title">
        {t(titleKey)}
      </Typography.Title3>
      {canCreate && (
        <>
          <Typography.Text className="job-candidate-summary__state-subtitle">{t(descriptionKey)}</Typography.Text>
          <Button
            className="job-candidate-summary__state-generate"
            type={BUTTON_TYPES.PRIMARY}
            icon={<Ai />}
            onClick={onGenerate}
          >
            {t('translation:job_candidate_summary.action_generate')}
          </Button>
        </>
      )}
    </div>
  );
};

const SummaryLoading: React.FC<{ onCancel: () => void }> = ({ onCancel }) => {
  const { t } = useTranslation();

  return (
    <>
      <LoadingCard
        title={t('translation:job_candidate_summary.loading_title')}
        description={t('translation:job_candidate_summary.loading_description')}
        action={
          <Button type={BUTTON_TYPES.SECONDARY} variant="grey" onClick={onCancel}>
            {t('translation:job_candidate_summary.action_cancel')}
          </Button>
        }
      />
      <AtsSkeleton count={4} height={12} className="summary-skeleton" />
    </>
  );
};

const SummaryAcceptedIcon: React.FC<React.SVGProps<SVGSVGElement>> = props => (
  <Accepted {...props} viewBox={SUMMARY_ACCEPTED_ICON_VIEWBOX} />
);

const SummaryInfoIcon: React.FC<React.SVGProps<SVGSVGElement>> = ({ className, ...props }) => (
  <Info {...props} className={[className, 'job-candidate-summary__list-icon--yellow'].filter(Boolean).join(' ')} />
);

const SummaryIconList: React.FC<SummaryIconListProps> = ({ items, icon: Icon }) => (
  <ul className="job-candidate-summary__list">
    {items.map(item => (
      <li key={item} className="job-candidate-summary__list-item">
        <Icon className="job-candidate-summary__list-icon" aria-hidden />
        <Typography.Text>{item}</Typography.Text>
      </li>
    ))}
  </ul>
);

const SummaryGenerated: React.FC<{ summary: CandidateSummary }> = ({ summary }) => {
  const { t } = useTranslation();
  const { content, source_counts } = summary;
  const generatedFrom = formatSummarySources(source_counts, t);

  return (
    <div>
      <div className="job-candidate-summary__data-section">
        <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
          {t('translation:job_candidate_summary.overall_summary')}
        </Typography.Title3>
        <Typography.Text>{content.overall_summary || EMPTY_SUMMARY_VALUE}</Typography.Text>
        <Divider />
      </div>
      <div className="job-candidate-summary__data-section">
        <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
          {t('translation:job_candidate_summary.interview_feedback')}
        </Typography.Title3>
        {content.interview_feedback?.length > 0
          ? content.interview_feedback.map(({ title, text }) => (
              <Typography.Text key={title}>
                <span className="job-candidate-summary__interview-label">{title}:</span> {text}
              </Typography.Text>
            ))
          : EMPTY_SUMMARY_VALUE}
        <Divider />
      </div>
      <div className="job-candidate-summary__data-section">
        <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
          {t('translation:job_candidate_summary.positive_decisions')}
        </Typography.Title3>
        {content.positive_decisions?.length > 0 ? (
          <SummaryIconList items={content.positive_decisions} icon={SummaryAcceptedIcon} />
        ) : (
          EMPTY_SUMMARY_VALUE
        )}
        <Divider />
      </div>
      <div className="job-candidate-summary__data-section">
        <Typography.Title3 weight={TYPOGRAPHY_WEIGHT.BOLD}>
          {t('translation:job_candidate_summary.risks_concerns')}
        </Typography.Title3>
        {content.risks_concerns?.length > 0 ? (
          <SummaryIconList items={content.risks_concerns} icon={SummaryInfoIcon} />
        ) : (
          EMPTY_SUMMARY_VALUE
        )}
        <Divider />
      </div>
      <div>
        <Typography.Label weight={TYPOGRAPHY_WEIGHT.NORMAL} className="job-candidate-summary__metadata-item">
          {t('translation:job_candidate_summary.generated_from')} {generatedFrom}.
        </Typography.Label>
        {summary.is_edited && (
          <Typography.Label weight={TYPOGRAPHY_WEIGHT.NORMAL} className="job-candidate-summary__metadata-item">
            {t('translation:job_candidate_summary.last_edited_by', {
              editor: summary.edited_by?.full_name,
              date: dateFormatter(summary.edited_at, 'MMM DD, YYYY'),
            } as Record<string, unknown>)}
          </Typography.Label>
        )}
      </div>
    </div>
  );
};

export const JobCandidateSummary: React.FC<JobCandidateSummaryProps> = ({
  editing,
  stepName,
  isApprover,
  isResponsibleRecruiter,
  onEditingChange,
}) => {
  const { t } = useTranslation();
  const { candidate: candidateId, id: jobId } = useParams<{ candidate: string; id: string }>();
  const ability = useAbility();
  const dispatch = useAppDispatch();
  const candidateSummary = useAppSelector(getCandidateSummary);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [openRegenerate, setOpenRegenerate] = useState<boolean>(false);
  const generationAbortController = useRef<AbortController>();
  const isCurrentCandidate = candidateSummary.candidateId === candidateId;
  const currentSummary = isCurrentCandidate ? candidateSummary.data : null;
  const currentSummaryStatus = isCurrentCandidate ? candidateSummary.status : SUMMARY_STATUSES.EMPTY;
  const loading = isCurrentCandidate && candidateSummary.requestStatus === 'loading';
  const candidateSummarySubject = subject(SUBJECTS.AI_CANDIDATE_SUMMARY, {
    isApprover,
    isResponsibleRecruiter,
  });
  const canCreate = ability.can(ACTIONS.CREATE, candidateSummarySubject);
  const toggleExpanded = () => {
    if (!editing) setExpanded(value => !value);
  };

  useEffect(() => {
    if (candidateId && jobId) dispatch(loadCandidateSummary(candidateId, jobId));

    return () => {
      if (generationAbortController.current) {
        generationAbortController.current.abort();
        generationAbortController.current = undefined;
      }
    };
  }, [candidateId, jobId, dispatch]);

  const onGenerateSummary = async () => {
    if (!candidateId || !jobId) return;

    generationAbortController.current?.abort();
    const controller = new AbortController();
    generationAbortController.current = controller;

    try {
      const summary = await dispatch(generateCandidateSummary(candidateId, jobId, controller.signal));
      if (summary && !controller.signal.aborted) {
        notification({
          message: t('translation:job_candidate_summary.summary_created_success_message'),
          type: NOTIFICATION_TYPES.SUCCESS,
        });
      }
    } finally {
      if (generationAbortController.current === controller) {
        generationAbortController.current = undefined;
      }
    }
  };

  const onCancelGeneration = () => {
    generationAbortController.current?.abort();
    generationAbortController.current = undefined;
    dispatch(clearCandidateSummary());
  };

  const onRegenerateSummary = () => {
    setOpenRegenerate(false);
    dispatch(clearCandidateSummary());
    onGenerateSummary();
  };

  const onOpenRegeneratePopup = (e: MouseEvent) => {
    e.stopPropagation();
    setOpenRegenerate(true);
  };

  const onOpenEdit = (e: MouseEvent) => {
    e.stopPropagation();
    setExpanded(true);
    onEditingChange(true);
  };

  const onCancelEdit = () => onEditingChange(false);

  const onSaveEdit = (values: SummaryEditValues) => {
    if (!candidateId || !jobId || !currentSummary || loading) return;

    const normalizeList = (items: string[]) => items.map(item => item.trim()).filter(Boolean);
    const interviewFeedback = values.interview_feedback.reduce<CandidateSummary['content']['interview_feedback']>(
      (result, { text }, index) => {
        const normalizedText = text.trim();
        if (!normalizedText) return result;

        result.push({
          title: currentSummary.content.interview_feedback[index]?.title,
          text: normalizedText,
        });
        return result;
      },
      []
    );

    dispatch(
      updateCandidateSummary(candidateId, jobId, {
        overall_summary: values.overall_summary.trim(),
        interview_feedback: interviewFeedback,
        positive_decisions: normalizeList(values.positive_decisions),
        risks_concerns: normalizeList(values.risks_concerns),
      })
    ).then(summary => {
      if (!summary) return;
      onEditingChange(false);
      notification({
        message: t('translation:job_candidate_summary.summary_updated_success_message'),
        type: NOTIFICATION_TYPES.SUCCESS,
      });
    });
  };

  return (
    <div className="job-candidate-summary">
      <div className={`job-candidate-summary__header${editing ? ' editing' : ''}`} role="none" onClick={toggleExpanded}>
        <SummaryHeading
          status={currentSummaryStatus}
          generatedAt={currentSummary?.generated_at}
          showStatus={!editing}
        />
        {!editing && (
          <div className="job-candidate-summary__actions">
            {expanded && currentSummaryStatus === SUMMARY_STATUSES.GENERATED && currentSummary && (
              <>
                {canCreate && (
                  <Button icon={<Undo />} type={BUTTON_TYPES.TEXT} variant="grey" onClick={onOpenRegeneratePopup}>
                    {t('translation:job_candidate_summary.action_regenerate')}
                  </Button>
                )}
                <Can I={ACTIONS.UPDATE} a={candidateSummarySubject as never}>
                  <Button icon={<Edit />} type={BUTTON_TYPES.TEXT} variant="grey" onClick={onOpenEdit}>
                    {t('translation:job_candidate_summary.action_edit')}
                  </Button>
                </Can>
              </>
            )}
            <div className={`job-candidate-summary__icon${expanded ? ' expanded' : ''}`}>
              <Vector />
            </div>
          </div>
        )}
      </div>
      <div className={`job-candidate-summary__content${expanded || editing ? ' expanded' : ''}`}>
        <div className="job-candidate-summary__content-inner">
          {editing && currentSummary ? (
            <SummaryEdit
              summary={currentSummary}
              stepName={stepName}
              loading={loading}
              onCancel={onCancelEdit}
              onSave={onSaveEdit}
            />
          ) : loading ? (
            <SummaryLoading onCancel={generationAbortController.current ? onCancelGeneration : undefined} />
          ) : currentSummary ? (
            <SummaryGenerated summary={currentSummary} />
          ) : (
            <SummaryState
              state={currentSummaryStatus as SummaryState}
              canCreate={canCreate}
              onGenerate={onGenerateSummary}
            />
          )}
        </div>
      </div>

      <PopupRegenerateCandidateSummary
        open={openRegenerate}
        onClose={() => setOpenRegenerate(false)}
        onConfirm={onRegenerateSummary}
      />
    </div>
  );
};
